import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useI18n } from '@/contexts/I18nContext';
import { useTechniques } from '@/shared/hooks/useTechniques';

import { INote } from '@/features/calendar/hooks/useNotes';

interface INotesFiltersProps {
  noteType: 'all' | 'personal' | 'class' | 'feedback';
  category: string | null;
  technique: string | null;
  student: string | null;
  isInstructor: boolean;
  onNoteTypeChange: (type: 'all' | 'personal' | 'class' | 'feedback') => void;
  onCategoryChange: (category: string | null) => void;
  onTechniqueChange: (technique: string | null) => void;
  onStudentChange: (studentId: string | null) => void;
  filteredNotes: INote[]; // Notes filtered by noteType and category (but not technique)
}

export const NotesFilters: React.FC<INotesFiltersProps> = ({
  noteType,
  category,
  technique,
  student,
  isInstructor,
  onNoteTypeChange,
  onCategoryChange,
  onTechniqueChange,
  onStudentChange,
  filteredNotes,
}) => {
  const { t, currentLanguage } = useI18n();
  const { data: techniqueCategories, isLoading: techniquesLoading } = useTechniques();
  const [availableTechniques, setAvailableTechniques] = useState<any[]>([]);
  const [availableCategories, setAvailableCategories] = useState<any[]>([]);
  const [availableStudents, setAvailableStudents] = useState<Array<{ id: string; name: string }>>([]);

  // Calculate available categories based on filtered notes
  useEffect(() => {
    if (techniqueCategories && filteredNotes.length > 0) {
      // Extract unique categories from filtered notes
      const categoriesInNotes = new Set<string>();
      
      filteredNotes.forEach((note) => {
        if (note.techniques && Array.isArray(note.techniques)) {
          note.techniques.forEach((tech: any) => {
            if (tech.category) {
              categoriesInNotes.add(tech.category);
            }
          });
        }
      });

      // Get full category objects that appear in the notes
      const available = (techniqueCategories as any[])?.filter(cat => 
        categoriesInNotes.has(cat.name)
      );
      setAvailableCategories(available);
    } else if (filteredNotes.length === 0) {
      // No notes available
      setAvailableCategories([]);
    } else {
      // No technique categories loaded yet, or no notes with techniques
      setAvailableCategories([]);
    }
  }, [techniqueCategories, filteredNotes]);

  // Calculate available students: only partners from notes where the user was the instructor.
  // Regular training partners (practitioner role) are intentionally excluded.
  useEffect(() => {
    const studentsMap = new Map<string, string>();
    filteredNotes
      .filter((note) => note.is_instructor_class === true)
      .forEach((note) => {
        note.partners?.forEach((p) => {
          if (p.partner_user_id && p.partner_name) {
            studentsMap.set(p.partner_user_id, p.partner_name);
          }
        });
      });
    setAvailableStudents(
      Array.from(studentsMap.entries()).map(([id, name]) => ({ id, name }))
    );
  }, [filteredNotes]);

  // Calculate available techniques based on filtered notes
  useEffect(() => {
    if (category && techniqueCategories && filteredNotes.length > 0) {
      // Extract unique technique names from filtered notes that match the selected category
      const techniquesInNotes = new Set<string>();
      
      filteredNotes.forEach((note) => {
        if (note.techniques && Array.isArray(note.techniques)) {
          note.techniques.forEach((tech: any) => {
            // Check if the technique's category matches the selected category
            if (tech.category === category) {
              techniquesInNotes.add(tech.name);
            }
          });
        }
      });

      // Get full technique objects from the category that appear in the notes
      const selectedCategory = (techniqueCategories as any[])?.find(cat => cat.name === category);
      if (selectedCategory && selectedCategory.techniques) {
        const available = selectedCategory.techniques.filter((tech: any) => 
          techniquesInNotes.has(tech.name)
        );
        setAvailableTechniques(available);
      } else {
        setAvailableTechniques([]);
      }
    } else if (category && techniqueCategories) {
      // If no notes match, show all techniques from the category (fallback)
      const selectedCategory = (techniqueCategories as any[])?.find(cat => cat.name === category);
      if (selectedCategory) {
        setAvailableTechniques(selectedCategory.techniques || []);
      } else {
        setAvailableTechniques([]);
      }
    } else {
      setAvailableTechniques([]);
    }
  }, [category, techniqueCategories, filteredNotes]);

  return (
    <div className="flex flex-col gap-3 mb-6">
      {/* Filter 0: Student — visible to instructors only.
          FEATURE_FLAG: PARTNER_FILTER_FOR_STUDENTS — extend to non-instructors when ready */}
      {isInstructor && (
        <div>
          <label className="text-xs text-white/60 mb-1 block">
            {t('calendar.my_notes.filter_student')}
          </label>
          <Select
            value={student || 'all'}
            onValueChange={(value) => onStudentChange(value === 'all' ? null : value)}
            disabled={availableStudents.length === 0}
          >
            <SelectTrigger className="w-full bg-[#281B1B] border-white/20 text-white disabled:opacity-50">
              <SelectValue placeholder={t('calendar.my_notes.select_student')} />
            </SelectTrigger>
            <SelectContent className="bg-black border-0 py-2 space-y-1">
              <SelectItem value="all" className="text-white truncate">
                {t('calendar.my_notes.all_students')}
              </SelectItem>
              {availableStudents.length === 0 ? (
                <SelectItem value="no-students" disabled className="text-white/40 italic">
                  {t('calendar.my_notes.no_students_available')}
                </SelectItem>
              ) : (
                availableStudents.map((s) => (
                  <SelectItem key={s.id} value={s.id} className="text-white truncate">
                    {s.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Filter 1: Note Type */}
      <div>
        <label className="text-xs text-white/60 mb-1 block">
          {t('calendar.my_notes.filter_note_type')}
        </label>
        <Select
          value={noteType}
          onValueChange={(value) => onNoteTypeChange(value as 'all' | 'personal' | 'class' | 'feedback')}
        >
          <SelectTrigger className="w-full bg-[#281B1B] border-white/20 text-white">
            <SelectValue placeholder={t('calendar.my_notes.select_note_type')} />
          </SelectTrigger>
          <SelectContent className="bg-black border-0 py-2 space-y-1">
            <SelectItem value="all" className="text-white truncate">
              {t('calendar.my_notes.all_notes')}
            </SelectItem>
            <SelectItem value="class" className="text-white truncate">
              {t('calendar.my_notes.class_notes')}
            </SelectItem>
            <SelectItem value="personal" className="text-white truncate">
              {t('calendar.my_notes.personal_notes')}
            </SelectItem>
            <SelectItem value="feedback" className="text-white truncate">
              {t('calendar.my_notes.feedback')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Filter 2: Technique Category */}
      <div>
        <label className="text-xs text-white/60 mb-1 block">
          {t('calendar.my_notes.filter_category')}
        </label>
        <Select
          value={category || 'all'}
          onValueChange={(value) => onCategoryChange(value === 'all' ? null : value)}
          disabled={techniquesLoading || availableCategories.length === 0}
        >
          <SelectTrigger className="w-full bg-[#281B1B] border-white/20 text-white disabled:opacity-50">
            <SelectValue placeholder={t('calendar.my_notes.select_category')} />
          </SelectTrigger>
          <SelectContent className="bg-black border-0 py-2 space-y-1">
            <SelectItem value="all" className="text-white truncate">
              {t('calendar.my_notes.all_categories')}
            </SelectItem>
            {techniquesLoading ? (
              <SelectItem value="loading" disabled className="text-white/60">
                {t('loading.techniques')}
              </SelectItem>
            ) : availableCategories.length === 0 ? (
              <SelectItem value="no-categories" disabled className="text-white/40 italic">
                {t('calendar.my_notes.no_categories_available')}
              </SelectItem>
            ) : (
              availableCategories.map((cat) => (
                <SelectItem key={cat.name} value={cat.name} className="text-white truncate">
                  {currentLanguage === 'pt' && cat.name_br ? cat.name_br : cat.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        {!techniquesLoading && availableCategories.length === 0 && filteredNotes.length > 0 && (
          <p className="text-xs text-white/40 mt-1 italic">
            {t('calendar.my_notes.no_categories_in_notes')}
          </p>
        )}
      </div>

      {/* Filter 3: Technique */}
      <div>
        <label className="text-xs text-white/60 mb-1 block">
          {t('calendar.my_notes.filter_technique')}
        </label>
        <Select
          value={technique || 'all'}
          onValueChange={(value) => onTechniqueChange(value === 'all' ? null : value)}
          disabled={!category || availableTechniques.length === 0}
        >
          <SelectTrigger className="w-full bg-[#281B1B] border-white/20 text-white disabled:opacity-50">
            <SelectValue placeholder={t('calendar.my_notes.select_technique')} />
          </SelectTrigger>
          <SelectContent className="bg-black border-0 py-2 space-y-1">
            <SelectItem value="all" className="text-white truncate">
              {t('calendar.my_notes.all_techniques')}
            </SelectItem>
            {availableTechniques.map((tech) => (
              <SelectItem key={tech.id} value={tech.name} className="text-white truncate">
                {currentLanguage === 'pt' && tech.name_br ? tech.name_br : tech.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

